import { useEffect, useState } from 'react';

import { PipelineTable } from '../components/pipeline/PipelineTable';
import { apiClient, type Custodian, type Opportunity } from '../lib/api';

/**
 * Manager-facing view of the opportunities pipeline.
 *
 * The data is fetched from /api/v1/manager/opportunities which:
 *  - replaces the opportunity `name` with a soft 3+3 pseudonym
 *    (e.g. "BRA-ALP" for "Bravo · Alpha")
 *  - strips PII-adjacent fields (passport_origin, referred_by,
 *    reverse_solicitation_evidence, owner_rm identity)
 *
 * The PipelineTable is reused verbatim in readOnly mode: no inline edits,
 * no link to the detail page, no Excel export to disk (managers don't get
 * the unfiltered RM view).
 *
 * SECURITY: any future field added to the manager payload must pass through
 * the §6.3 / §11.3 review process. Cross-check apps/api/src/modules/manager.
 */
export function ManagerOpportunitiesPage() {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [custodians, setCustodians] = useState<Custodian[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rows, banks] = await Promise.all([
          apiClient.listManagerOpportunities(),
          apiClient.listCustodians().catch(() => [] as Custodian[]),
        ]);
        if (!cancelled) {
          setOpportunities(rows);
          setCustodians(banks);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="px-8 pt-10">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase">— Manager view</p>
          <h1 className="mt-2 font-display text-4xl text-graphite font-semibold">
            Pipeline (anonymized)
          </h1>
          <p className="mt-2 text-sm text-ink-secondary">
            {opportunities.length} opportunities · names pseudonymized (LAST-FIR) · read-only · PII
            fields stripped at the API boundary
          </p>
        </div>
        <span
          className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full font-medium"
          style={{ background: '#1A2832', color: '#FFFFFF' }}
        >
          Read-only · Manager
        </span>
      </header>

      {loading && <div className="px-8 pb-10 text-sm text-ink-tertiary">Loading…</div>}
      {error && (
        <div className="px-8 pb-10 text-sm text-semantic-error" role="alert">
          {error}
        </div>
      )}
      {!loading && !error && (
        <PipelineTable
          opportunities={opportunities}
          custodians={custodians}
          // PATCH is gated server-side; this no-op is just to satisfy the
          // shared component contract.
          onPatched={() => {}}
          readOnly
        />
      )}
    </div>
  );
}
